/**
 * Copyright 2013-present, Novivia, Inc.
 * All rights reserved.
 */

/**
 * A module representing an database error.
 * @module utilities/errors/database
 */
"use strict";

var deprecate = require("depd")("@auex/utilities");
var util = require("util");
var WError = require("verror").WError;

/**
 * Represents a database error.
 *
 * @constructor
 * @param {Error|null} cause - The underlying error that caused this one.
 * @param {String} message - A custom error message.
 */
function DatabaseError(cause, message) {
  deprecate("'DatabaseError' is deprecated in this module. Please use" + " '@novivia/utilities/types/errors/DataStoreError' instead.");
  this.name = "DatabaseError";
  this.message = message || "Internal database error.";

  Error.captureStackTrace(this, this.constructor);
  if (cause) {
    this.cause(cause);
  }
}
util.inherits(DatabaseError, WError);

/**
 * Represents a duplicated field database error.
 *
 * @constructor
 * @param {Error|null} cause - The underlying error that caused this one.
 * @param {String} message - A custom error message.
 * @param {String} propertyName - Name of the field containing the duplicated
 *                                value.
 * @param {String} propertyValue - Duplicated value itself.
 */
function DuplicatedFieldError(cause, message, propertyName, propertyValue) {
  deprecate("'DuplicatedFieldError' is deprecated in this module. Please use" + " '@novivia/utilities/types/errors/DuplicateFieldError' instead.");
  this.name = "DuplicatedFieldError";
  this.message = message || "An entry with the same value already exists.";
  this.propertyName = propertyName;
  this.propertyValue = propertyValue;

  Error.captureStackTrace(this, this.constructor);
  if (cause) {
    this.cause(cause);
  }
}
util.inherits(DuplicatedFieldError, DatabaseError);

/**
 * Represents an invalid field database error.
 *
 * @constructor
 * @param {Error|null} cause - The underlying error that caused this one.
 * @param {String} message - A custom error message.
 * @param {String} propertyName - Name of the field provided an invalid value.
 * @param {String} propertyValue - Invalid value itself.
 */
function InvalidFieldError(cause, message, propertyName, propertyValue) {
  deprecate("'InvalidFieldError' is deprecated in this module. Please use" + " '@novivia/utilities/types/errors/InvalidFieldError' instead.");
  this.name = "InvalidFieldError";
  this.message = message || "A field was provided invalid data.";
  this.propertyName = propertyName;
  this.propertyValue = propertyValue;

  Error.captureStackTrace(this, this.constructor);
  if (cause) {
    this.cause(cause);
  }
}
util.inherits(InvalidFieldError, DatabaseError);

/**
 * Represents a required field database error.
 *
 * @constructor
 * @param {Error|null} cause - The underlying error that caused this one.
 * @param {String} message - A custom error message.
 * @param {String} propertyName - Name of the field not provided a value.
 */
function RequiredFieldError(cause, message, propertyName) {
  deprecate("'RequiredFieldError' is deprecated in this module. Please use" + " '@novivia/utilities/types/errors/MissingFieldError' instead.");
  this.name = "RequiredFieldError";
  this.message = message || "A required field is missing.";
  this.propertyName = propertyName;

  Error.captureStackTrace(this, this.constructor);
  if (cause) {
    this.cause(cause);
  }
}
util.inherits(RequiredFieldError, DatabaseError);

/**
 * Represents a resource not found database error.
 *
 * @constructor
 * @param {Error|null} cause - The underlying error that caused this one.
 * @param {String} message - A custom error message.
 * @param {String} propertyName - Type of the resource not found.
 */
function ResourceNotFoundError(cause, message, propertyName) {
  deprecate("'ResourceNotFoundError' is deprecated in this module. Please use" + " '@novivia/utilities/types/errors/ResourceNotFoundError' instead.");
  this.name = "ResourceNotFoundError";
  this.message = message || "The requested resource was not found.";
  this.propertyName = propertyName;

  Error.captureStackTrace(this, this.constructor);
  if (cause) {
    this.cause(cause);
  }
}
util.inherits(ResourceNotFoundError, DatabaseError);

/**
 * Represents a resource creation failure database error.
 *
 * @constructor
 * @param {Error|null} cause - The underlying error that caused this one.
 * @param {String} message - A custom error message.
 * @param {String} propertyName - Type of the resource that couldn't be created.
 */
function ResourceCreationError(cause, message, propertyName) {
  deprecate("'ResourceCreationError' is deprecated in this module. Please use" + " '@novivia/utilities/types/errors/ResourceCreationError' instead.");
  this.name = "ResourceCreationError";
  this.message = message || "The resource couldn't be created.";
  this.propertyName = propertyName;

  Error.captureStackTrace(this, this.constructor);
  if (cause) {
    this.cause(cause);
  }
}
util.inherits(ResourceCreationError, DatabaseError);

/**
 * Represents a read only resource database error.
 *
 * @constructor
 * @param {Error|null} cause - The underlying error that caused this one.
 * @param {String} message - A custom error message.
 * @param {String} propertyName - Type of the resource that is read-only.
 * @param {String} propertyValue - ID of the resource that is read-only.
 */
function ResourceReadOnlyError(cause, message, propertyName, propertyValue) {
  deprecate("'ResourceReadOnlyError' is deprecated in this module. Please use" + " '@novivia/utilities/types/errors/ReadOnlyError' instead.");
  this.name = "ResourceReadOnlyError";
  this.message = message || "A write attempt was made on a read-only resource.";
  this.propertyName = propertyName;
  this.propertyValue = propertyValue;

  Error.captureStackTrace(this, this.constructor);
  if (cause) {
    this.cause(cause);
  }
}
util.inherits(ResourceReadOnlyError, DatabaseError);

module.exports = exports = DatabaseError;

DatabaseError.DuplicatedFieldError = DuplicatedFieldError;
DatabaseError.InvalidFieldError = InvalidFieldError;
DatabaseError.RequiredFieldError = RequiredFieldError;
DatabaseError.ResourceNotFoundError = ResourceNotFoundError;
DatabaseError.ResourceCreationError = ResourceCreationError;
DatabaseError.ResourceReadOnlyError = ResourceReadOnlyError;


//////////////////
// WEBPACK FOOTER
// /private/var/folders/n9/yczdnstj3wdc9q5tzm6d45c00000gp/T/maison-encans-auex-website/~/@auex/utilities/lib/errors/database.js
// module id = 869
// module chunks = 6